import type { Component } from 'solid-js'
import { Show } from 'solid-js'
import type {
  ComparedValue,
  UsersChatItem,
  UsersEventsItem,
  UsersForumItem,
  UsersMediaItem
} from '../../../features/engagement/domain/models/engagement-users'
import type { EngagementUserType } from './EngagementUsersTable'
import ValueProgressCell from './custom-cells/ValueProgressCell'
import UserInfoCell from './custom-cells/UserInfoCell'
import { KPI, KPIType } from '../../../features/analytics/domain/models/kpi'
import { NumericalDataType } from '../../../shared/models/numerical-data-type'

export interface EngagementUsersTableRowProps {
  readonly user: EngagementUserType
  readonly rowNumber: number
  readonly category?: 'media' | 'events' | 'forum' | 'chat'
}

const EngagementUsersTableRow: Component<EngagementUsersTableRowProps> = (props) => {
  const kpi = (id: KPIType, value: ComparedValue) =>
    new KPI(id, value.current, NumericalDataType.Units, value.diff, value.previous)

  const metricWidthClass = () => {
    // 5 metric columns (events) needs a slightly narrower width to fit better.
    if (props.category === 'events') return 'w-[152px]'
    return 'w-[202px]'
  }

  return (
    <tr class="hover:bg-gray-50">
      <td class="whitespace-nowrap border-r border-gray-200 px-3   py-3.5 text-center text-sm text-gray-700">
        {props.rowNumber}
      </td>
      <td class="border-r border-gray-200 px-4 py-4 min-w-[260px]">
        <UserInfoCell user={props.user} />
      </td>

      {/* Media (also default) */}
      <Show when={props.category === 'media' || !props.category}>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.SongsPlayed, (props.user as UsersMediaItem).songs_played)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.VideosViewed, (props.user as UsersMediaItem).videos_viewed)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell
            kpi={kpi(KPIType.LivestreamParticipation, (props.user as UsersMediaItem).livestream_participation)}
          />
        </td>
      </Show>

      {/* Events */}
      <Show when={props.category === 'events'}>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.BuyNowClicks, (props.user as UsersEventsItem).buy_now_clicks)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.PresaleCodes, (props.user as UsersEventsItem).presale_codes)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.MeetAndGreet, (props.user as UsersEventsItem).meet_and_greet)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.Vip, (props.user as UsersEventsItem).vip)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.Rsvp, (props.user as UsersEventsItem).rsvp)} />
        </td>
      </Show>

      {/* Forum */}
      <Show when={props.category === 'forum'}>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.ForumViews, (props.user as UsersForumItem).views)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.ForumPosts, (props.user as UsersForumItem).posts)} />
        </td>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.ForumComments, (props.user as UsersForumItem).comments)} />
        </td>
      </Show>

      {/* Chat */}
      <Show when={props.category === 'chat'}>
        <td class={metricWidthClass()}>
          <ValueProgressCell kpi={kpi(KPIType.Messages, (props.user as UsersChatItem).messages)} />
        </td>
      </Show>
    </tr>
  )
}

export default EngagementUsersTableRow
